import { useGroupLifeCoverageSchema } from '@/features/quotes/hooks/group-life/use-coverage-schema.hook';
import type { GroupLifeQuoteDetails } from '@/services/group-life';
import { useMemo } from 'react';
import { useGroupLifeProposalLives } from '../group-life';
import {
  buildCoverageNameByCode,
  mapCommercializationRows,
  mapDemonstrativeRows,
  mapInsuranceCard,
  mapPremiumValues,
  mapStipulantAccordions,
  mapStipulantCard,
  resolvePlanCoverages,
} from '../../components/pme/utils/hydrate-proposal.util';
import { usePmeUnitPurePremium } from './use-pme-unit-pure-premium.hook';

type Args = {
  details?: GroupLifeQuoteDetails;
  productCode?: string;
  effectiveDate?: string;
  /** Já existe envelope DocuSign — muda o rótulo de status do card de prêmio. */
  hasSignaturesStarted?: boolean;
  /** Assistência sem capital segurado fica sem valor — o Resumo não exibe o
   *  preço por vida dela. */
  hideAssistanceValue?: boolean;
};

/**
 * Detalhamento da proposta PME: prêmio, cards de seguro/estipulante,
 * estipulantes com coberturas, vidas, demonstrativo e comercialização.
 *
 * Mora num hook porque as MESMAS seções aparecem no Resumo e na Assinatura — a
 * tela de Assinatura mostra o detalhamento completo, no desenho do Capital
 * Global, com os documentos em coleta logo abaixo. Quem renderiza é o
 * `PmeProposalDetailsSections`, que só recebe o resultado daqui por props.
 */
export function usePmeProposalDetailsViewModel({
  details,
  productCode,
  effectiveDate,
  hasSignaturesStarted = false,
  hideAssistanceValue = false,
}: Args) {
  const schemaQuery = useGroupLifeCoverageSchema({
    productCode: productCode || undefined,
  });

  const {
    lives,
    total: totalLives,
    totalMemberCapital,
  } = useGroupLifeProposalLives(details?.policyId, details);

  const premium = useMemo(
    () =>
      mapPremiumValues(details?.calculation, {
        statusPortal: details?.statusPortal,
        hasSignaturesStarted,
      }),
    [details, hasSignaturesStarted]
  );

  const coverageNameByCode = useMemo(
    () => buildCoverageNameByCode(schemaQuery.data),
    [schemaQuery.data]
  );

  const demonstrativeRows = useMemo(
    () => mapDemonstrativeRows(details?.calculation, coverageNameByCode),
    [details, coverageNameByCode]
  );

  // Assistência não tem capital: o valor dela na tabela sai do preço por vida.
  // Sem `details` a busca da tarifação fica desligada e o mapa vem vazio — a
  // assistência fica sem valor.
  const unitPurePremiumByCoverage = usePmeUnitPurePremium(
    hideAssistanceValue ? undefined : details
  );

  const accordions = useMemo(
    () =>
      mapStipulantAccordions(details, coverageSumInsured =>
        resolvePlanCoverages(schemaQuery.data, details, {
          memberCapitalOverride: totalMemberCapital,
          livesCount: totalLives,
          unitPurePremiumByCoverage,
          coverageSumInsured,
        })
      ),
    [
      schemaQuery.data,
      details,
      totalMemberCapital,
      totalLives,
      unitPurePremiumByCoverage,
    ]
  );

  const commercializationRows = useMemo(
    () => mapCommercializationRows(details),
    [details]
  );

  const insuranceCard = useMemo(
    () => mapInsuranceCard(details?.stipulator, effectiveDate),
    [details, effectiveDate]
  );

  const stipulantCard = useMemo(
    () => mapStipulantCard(details?.stipulator),
    [details]
  );

  return {
    premium,
    insuranceCard,
    stipulantCard,
    accordions,
    demonstrativeRows,
    commercializationRows,
    lives,
    totalLives,
    totalMemberCapital,
  };
}

export type PmeProposalDetails = ReturnType<
  typeof usePmeProposalDetailsViewModel
>;
